// FILE: src/services/importService.js
//
// Pure utility functions for the Bulk Import workflow.
// Nothing here touches React state or produces side-effects.
//
// ── Overview ──────────────────────────────────────────────────────────────────
//
//   parseJSON(text)
//     Parses raw file text into a candidate array.  Returns { questions, error }.
//
//   validateImportRow(q, seenIds, existingIds)
//     Validates one raw row.  Returns { status, errors }.
//     status values:
//       'valid'    — passes all checks; ready to import
//       'error'    — missing/invalid required fields, or duplicate id within file
//       'conflict' — id already exists in the active question store for this exam
//
//   buildPreview(rawQuestions, examId, existingQuestions)
//     Runs validateImportRow on every row and builds display metadata.
//     Returns an array of preview rows (see shape below).
//
//   normalizeImportRow(raw, examId)
//     Maps a valid raw row to the app's normalized question shape.
//
// ── Preview row shape ─────────────────────────────────────────────────────────
//
//   {
//     raw:          object    — the original parsed row
//     referenceId:  string    — derived display id, or '—' when id is absent
//     snippet:      string    — first 60 chars of question text
//     status:       'valid' | 'error' | 'conflict'
//     errors:       string[]  — human-readable error messages (empty for valid/conflict)
//     importStatus: string    — question lifecycle status that will be imported
//     reviewStatus: string    — question review status that will be imported
//   }
//
// ── Lifecycle defaults for imported questions ─────────────────────────────────
//
//   status       defaults to 'new'  (awaiting SME approval before going live)
//   reviewStatus defaults to 'unreviewed' (no human review completed yet)
//
//   Importers may explicitly supply 'active' for pre-approved questions, but
//   'new' + 'unreviewed' is the safe default for AI-generated batches.

import { generateReferenceId } from './questionService';
import {
  PMP_PERFORMANCE_DOMAINS,
  PMP_DELIVERY_CONTEXTS,
  PMI_ACP_ECO_DOMAINS,
  PMI_ACP_LEGACY_DOMAINS,
  PMI_ACP_LEGACY_TO_ECO_DOMAIN,
  PMI_ACP_PRACTICE_AREAS,
  COGNITIVE_LEVELS,
  DIFFICULTIES,
  EXAM_WEIGHTS,
} from '../config/examCategories';
import { getQuestionType } from '../questionTypes';
import { isKnownFocusArea, SECONDARY_DIMENSION_VALUES, isEcoDomainRequired, validateEcoDomain } from '../config/examCategories';
import { checkAssertedKey } from '../utils/letterReferences';

// Allowed values (must stay in sync with lifecycleConstants.js and DB constraints)
const VALID_STATUSES        = ['new', 'active', 'retired'];
const VALID_REVIEW_STATUSES = ['unreviewed', 'reported', 'in_review', 'reviewed'];

// PMP-only taxonomy enums (migration 008).  Non-PMP rows ignore these.
const VALID_PERFORMANCE_DOMAINS = PMP_PERFORMANCE_DOMAINS;
const VALID_DELIVERY_CONTEXTS   = PMP_DELIVERY_CONTEXTS;
// PMI-ACP ECO taxonomy enums (migration 032). Non-PMI-ACP rows ignore these.
const VALID_ECO_DOMAINS         = PMI_ACP_ECO_DOMAINS;
const VALID_PRACTICE_AREAS      = PMI_ACP_PRACTICE_AREAS;
// All-exam optional taxonomy.
const VALID_COGNITIVE_LEVELS    = COGNITIVE_LEVELS;
const VALID_DIFFICULTIES        = DIFFICULTIES;
const VALID_EXAM_WEIGHTS        = EXAM_WEIGHTS;
// PMP canonical metadata (migration 034). Free-form for now if the cert
// vocabulary file doesn't export it.
const VALID_PROCESS_GROUPS      = [
  'Initiating', 'Planning', 'Executing', 'Monitoring & Controlling', 'Closing',
];
const VALID_PMI_ACP_CATEGORIES  = PMI_ACP_ECO_DOMAINS;
const PMI_ACP_LEGACY_CATEGORY_SET = new Set(PMI_ACP_LEGACY_DOMAINS);

// Forward-maps a PMI-ACP `category` value to the four-domain ECO. Returns
// the input unchanged when it's already a four-domain value, the mapped
// value when it's a legacy seven-domain value, or null when it's neither.
function forwardMapPmiAcpCategory(value) {
  if (!value) return null;
  if (VALID_PMI_ACP_CATEGORIES.includes(value)) return value;
  if (PMI_ACP_LEGACY_CATEGORY_SET.has(value)) {
    return PMI_ACP_LEGACY_TO_ECO_DOMAIN[value] || null;
  }
  return null;
}

/**
 * Coalesces a metadata field — looks at q.metadata.<key> first, then falls
 * back to q.<key> at the root. Supports both the canonical nested-metadata
 * import format and the flat-field legacy/migration formats.
 */
function metaField(q, key) {
  if (q && q.metadata && typeof q.metadata === 'object' && key in q.metadata) {
    return q.metadata[key];
  }
  return q ? q[key] : undefined;
}

// ── Test-data guard ─────────────────────────────────────────────────────────
// IDs matching this pattern are reserved for test datasets (golden import,
// E2E smoke tests) and must never be imported into the production question bank.
//   \bGOLD\b  — original golden dataset prefix (word-boundary match)
//   -GI-      — golden import dataset prefix (e.g. pmp-GI-V1)
//   ^e2e-     — E2E smoke test prefix
const TEST_ID_PATTERN = /\bGOLD\b|-GI-|^e2e-/i;

// ── parseJSON ────────────────────────────────────────────────────────────────

/**
 * Attempts to parse raw JSON text into an array of candidate question objects.
 * Accepts either a JSON array or a single JSON object (which is wrapped in []).
 *
 * @param {string} text
 * @returns {{ questions: object[], error: string|null }}
 */
export function parseJSON(text) {
  try {
    const parsed = JSON.parse(text);
    const questions = Array.isArray(parsed) ? parsed : [parsed];
    return { questions, error: null };
  } catch (e) {
    return { questions: [], error: `Invalid JSON: ${e.message}` };
  }
}

// ── validateImportRow ────────────────────────────────────────────────────────

/**
 * Validates a single raw question row for import.
 *
 * Required fields: id, question (non-empty), choices (non-empty array),
 * correctAnswers (non-empty array where every entry is a valid choice id).
 *
 * Optional lifecycle fields (validated when present):
 *   status       — must be one of: 'new', 'active', 'retired'
 *   reviewStatus — must be one of: 'unreviewed', 'reported', 'in_review', 'reviewed'
 *
 * Duplicate detection:
 *   - If id appears more than once in the same import file, the second (and
 *     any subsequent) occurrences receive status 'error'.
 *   - If id already exists in the active question store, receives 'conflict'.
 *     Conflicts are not errors — the row is simply skipped during import.
 *
 * NOTE: validateImportRow is a pure read — it does NOT mutate seenIds.
 *       The caller (buildPreview) is responsible for adding each id to
 *       seenIds after validation.
 *
 * @param {object} q           - raw row from parsed JSON
 * @param {Set}    seenIds     - ids already validated earlier in the same file
 * @param {Set}    existingIds - ids already present in the active question store
 * @param {object} [options]
 * @param {string} [options.examId] - when 'pmp', PMP-only taxonomy fields are
 *                                    validated against their enums.  When any
 *                                    other value (or omitted), those fields
 *                                    pass through unvalidated.
 * @returns {{ status: 'valid'|'error'|'conflict', errors: string[] }}
 */
// Question types the importer accepts. Array-choice types keep the legacy
// checks below; object-choice types (Case Study Question Framework) delegate
// shape/key validation to the question-type registry — the single canonical
// spec shared with the case editor and the server-side scorer.
//   enhanced_matching: choices = { prompts: [{id,text}], options: [{id,text}] },
//                      correctAnswers = ['promptId:optionId', ...]
//   ordered_sequence:  choices = { items: [{id,text}] },
//                      correctAnswers = [itemId, ...] in the correct order
const ARRAY_CHOICE_TYPES  = ['multiple_choice', 'multiple_select', 'visual_interpretation'];
const OBJECT_CHOICE_TYPES = ['enhanced_matching', 'ordered_sequence'];
const VALID_IMPORT_TYPES  = [...ARRAY_CHOICE_TYPES, ...OBJECT_CHOICE_TYPES];

export function validateImportRow(q, seenIds, existingIds, options = {}) {
  const { examId } = options;
  const errors = [];

  // ── Required field checks ────────────────────────────────────────────────
  if (q.id === undefined || q.id === null || q.id === '') {
    errors.push('Missing required field: id');
  }
  if (!q.question || !String(q.question).trim()) {
    errors.push('Missing required field: question');
  }
  const qType = q.type || 'multiple_choice';
  if (q.type !== undefined && q.type !== null && !VALID_IMPORT_TYPES.includes(q.type)) {
    errors.push(`Invalid type "${q.type}". Allowed values: ${VALID_IMPORT_TYPES.join(', ')}`);
  }
  if (OBJECT_CHOICE_TYPES.includes(qType)) {
    // Registry-validated shapes (matching prompt/option maps, ordering items).
    const def = getQuestionType(qType);
    const registryErrors = def
      ? def.validate({ type: qType, choices: q.choices, correctAnswers: q.correctAnswers, image: q.image })
      : [`No registered validator for type "${qType}"`];
    registryErrors.forEach((msg) => errors.push(msg));
  } else if (!Array.isArray(q.choices) || q.choices.length === 0) {
    errors.push('Missing required field: choices (must be a non-empty array)');
  }
  if (!Array.isArray(q.correctAnswers) || q.correctAnswers.length === 0) {
    errors.push('Missing required field: correctAnswers (must be a non-empty array)');
  }

  // ── Optional lifecycle field validation ──────────────────────────────────
  if (q.status !== undefined && q.status !== null && !VALID_STATUSES.includes(q.status)) {
    errors.push(
      `Invalid status "${q.status}". Allowed values: ${VALID_STATUSES.join(', ')}`
    );
  }
  if (q.reviewStatus !== undefined && q.reviewStatus !== null && !VALID_REVIEW_STATUSES.includes(q.reviewStatus)) {
    errors.push(
      `Invalid reviewStatus "${q.reviewStatus}". Allowed values: ${VALID_REVIEW_STATUSES.join(', ')}`
    );
  }

  // ── PMP-only taxonomy validation (performanceDomain, deliveryContext) ───
  // These fields are OPTIONAL and are only enforced when examId === 'pmp'.
  // For non-PMP imports, any value passes through — preserving CPMAI import
  // compatibility with no taxonomy changes on that side.
  if (examId === 'pmp') {
    // PMP canonical metadata. Reads from q.metadata.<key> first, falls back
    // to q.<key> at root (migration 034 parity with PMI-ACP behavior).
    const pd = metaField(q, 'performanceDomain') ?? q.performanceDomain;
    if (pd !== undefined && pd !== null && pd !== '' && !VALID_PERFORMANCE_DOMAINS.includes(pd)) {
      errors.push(
        `Invalid performanceDomain "${pd}". Allowed values: ${VALID_PERFORMANCE_DOMAINS.join(', ')}`
      );
    }
    const dc = metaField(q, 'deliveryContext') ?? q.deliveryContext;
    if (dc !== undefined && dc !== null && dc !== '' && !VALID_DELIVERY_CONTEXTS.includes(dc)) {
      errors.push(
        `Invalid deliveryContext "${dc}". Allowed values: ${VALID_DELIVERY_CONTEXTS.join(', ')}`
      );
    }
    const pg = metaField(q, 'processGroup');
    if (pg !== undefined && pg !== null && pg !== '' && !VALID_PROCESS_GROUPS.includes(pg)) {
      errors.push(
        `Invalid metadata.processGroup "${pg}". Allowed values: ${VALID_PROCESS_GROUPS.join(', ')}`
      );
    }
  }

  // PMI-ACP canonical metadata. Reads from q.metadata.<key> first, falls
  // back to q.<key> at root. All optional; only the enum fields validate.
  if (examId === 'pmi-acp') {
    // category must be a 4-domain ECO value OR a legacy 7-domain value
    // (the legacy values are forward-mapped on normalize).
    const cat = q.category;
    if (cat !== undefined && cat !== null && cat !== ''
        && !VALID_PMI_ACP_CATEGORIES.includes(cat)
        && !PMI_ACP_LEGACY_CATEGORY_SET.has(cat)) {
      errors.push(
        `Invalid category "${cat}" for PMI-ACP. Allowed: ${VALID_PMI_ACP_CATEGORIES.join(', ')}` +
        ` (or a legacy seven-domain value which will be forward-mapped).`
      );
    }
    const ed = metaField(q, 'ecoDomain');
    if (ed !== undefined && ed !== null && ed !== '' && !VALID_ECO_DOMAINS.includes(ed)) {
      errors.push(
        `Invalid metadata.ecoDomain "${ed}". Allowed values: ${VALID_ECO_DOMAINS.join(', ')}`
      );
    }
    const pa = metaField(q, 'practiceArea');
    if (pa !== undefined && pa !== null && pa !== '' && !VALID_PRACTICE_AREAS.includes(pa)) {
      errors.push(
        `Invalid metadata.practiceArea "${pa}". Allowed values: ${VALID_PRACTICE_AREAS.join(', ')}`
      );
    }
    const ew = metaField(q, 'examWeight');
    if (ew !== undefined && ew !== null && ew !== '' && !VALID_EXAM_WEIGHTS.includes(ew)) {
      errors.push(
        `Invalid metadata.examWeight "${ew}". Allowed values: ${VALID_EXAM_WEIGHTS.join(', ')}`
      );
    }
  }

  // cognitiveLevel + difficulty apply to ALL exams (optional).
  const cl = metaField(q, 'cognitiveLevel');
  if (cl !== undefined && cl !== null && cl !== '' && !VALID_COGNITIVE_LEVELS.includes(cl)) {
    errors.push(
      `Invalid metadata.cognitiveLevel "${cl}". Allowed values: ${VALID_COGNITIVE_LEVELS.join(', ')}`
    );
  }
  const dif = metaField(q, 'difficulty');
  if (dif !== undefined && dif !== null && dif !== '' && !VALID_DIFFICULTIES.includes(dif)) {
    errors.push(
      `Invalid metadata.difficulty "${dif}". Allowed values: ${VALID_DIFFICULTIES.join(', ')}`
    );
  }

  // ── Phase 2: isExperimental flag (migration 012) ────────────────────────
  if (q.isExperimental !== undefined && q.isExperimental !== null
      && q.isExperimental !== true && q.isExperimental !== false) {
    errors.push(
      `Invalid isExperimental value "${q.isExperimental}". Must be a boolean (true | false).`
    );
  }

  // ── Phase 3: visual_interpretation type + image exhibit (migration 013) ──
  // For type='visual_interpretation', image.src is REQUIRED.  image.alt is
  // recommended but not required (UX/accessibility — handled as a soft warn
  // upstream when desired).  For any other type, image may be present and is
  // simply preserved; validateImportRow does not enforce it.
  if (q.type === 'visual_interpretation') {
    if (!q.image || typeof q.image !== 'object' || Array.isArray(q.image)) {
      errors.push(
        'visual_interpretation requires an image object: { src, alt? }.'
      );
    } else {
      if (!q.image.src || !String(q.image.src).trim()) {
        errors.push(
          'visual_interpretation requires image.src (path or URL).'
        );
      }
    }
  } else if (q.image !== undefined && q.image !== null) {
    // Non-visual question with an image — must still be a sane shape if present.
    if (typeof q.image !== 'object' || Array.isArray(q.image)) {
      errors.push('Invalid image value — must be an object { src, alt? } or null.');
    } else if (q.image.src !== undefined && typeof q.image.src !== 'string') {
      errors.push('Invalid image.src — must be a string path or URL.');
    }
  }

  // ── Secondary dimension (config-driven; PMOCP focus_area today) ─────────
  // Optional. Validated against SECONDARY_DIMENSION_VALUES for certs that
  // define one; certs without a secondary dimension pass anything through
  // (isKnownFocusArea returns true for them).
  const rawFocusArea = metaField(q, 'focusArea') ?? q.focusArea;
  if (rawFocusArea !== undefined && rawFocusArea !== null && rawFocusArea !== '') {
    if (typeof rawFocusArea !== 'string') {
      errors.push('Invalid focusArea — must be a string.');
    } else if (!isKnownFocusArea(examId, rawFocusArea.trim())) {
      const allowed = SECONDARY_DIMENSION_VALUES[examId] || [];
      errors.push(
        `Invalid focusArea "${rawFocusArea}" for ${examId}. Allowed values: ${allowed.join(', ')}`
      );
    }
  }

  // ── ECO domain (required for CPMAI; every other cert is exempt) ─────────
  // Unlike focusArea this is a publish precondition, so a missing value is an
  // error rather than an omission. Mirrors the questions_cpmai_eco_domain_ck
  // CHECK constraint so an import fails here with a readable message instead of
  // at the database with a constraint violation.
  if (isEcoDomainRequired(examId)) {
    const rawEco = metaField(q, 'ecoDomain') ?? q.ecoDomain;
    const { ok, error } = validateEcoDomain(
      examId,
      typeof rawEco === 'string' ? rawEco.trim() : rawEco
    );
    if (!ok) errors.push(error);
  }

  if (errors.length > 0) return { status: 'error', errors };

  // ── Cross-field validation (array-choice types only) ─────────────────────
  // Every correctAnswer must reference an existing choice id. Object-choice
  // types were already membership-checked by the registry above.
  if (ARRAY_CHOICE_TYPES.includes(qType)) {
    const choiceIds = new Set(q.choices.map((c) => c.id));
    const orphanAnswers = q.correctAnswers.filter((a) => !choiceIds.has(a));
    if (orphanAnswers.length > 0) {
      return {
        status: 'error',
        errors: [
          `correctAnswers references choice ids not present in choices: ${orphanAnswers.join(', ')}`,
        ],
      };
    }
    // Letter-reference consistency: an explanation that names a letter as the
    // correct answer must agree with correctAnswers. Same rule the DB enforces
    // on write (migration 103) — surfaced here so the import preview says why.
    const letterErr = checkAssertedKey({ choices: q.choices, correctAnswers: q.correctAnswers, explanation: q.explanation });
    if (letterErr) return { status: 'error', errors: [letterErr] };
  }

  const id = String(q.id);

  if (seenIds.has(id)) {
    return { status: 'error', errors: [`Duplicate id "${id}" within the import file`] };
  }

  if (existingIds.has(id)) {
    return { status: 'conflict', errors: [] };
  }

  return { status: 'valid', errors: [] };
}

// ── buildPreview ─────────────────────────────────────────────────────────────

/**
 * Builds the full preview row array for a parsed import batch.
 * Validates every row and computes display fields (referenceId, snippet).
 *
 * @param {object[]} rawQuestions      - array from parseJSON()
 * @param {string}   examId            - 'pmp' | 'cpmai'
 * @param {object[]} existingQuestions - questions currently in the active store
 * @returns {Array<{raw, referenceId, snippet, status, errors, importStatus, reviewStatus}>}
 */
export function buildPreview(rawQuestions, examId, existingQuestions, options = {}) {
  const { allowTestIds = false } = options;
  const existingIds = new Set(existingQuestions.map((q) => String(q.id)));
  const seenIds = new Set();

  return rawQuestions.map((q) => {
    let validation = validateImportRow(q, seenIds, existingIds, { examId });

    // ── Test-data guard (production only) ───────────────────────────────
    // Reject golden dataset / E2E smoke test questions before they reach
    // the store.  Tests pass { allowTestIds: true } to bypass this check.
    if (
      !allowTestIds &&
      validation.status === 'valid' &&
      q.id !== undefined && q.id !== null &&
      TEST_ID_PATTERN.test(String(q.id))
    ) {
      validation = {
        status: 'error',
        errors: [`ID "${q.id}" matches a test-data pattern (GOLD, -GI-, or e2e-). Test questions cannot be imported.`],
      };
    }

    // Register id for subsequent duplicate checks (only when id is present)
    if (q.id !== undefined && q.id !== null && q.id !== '') {
      seenIds.add(String(q.id));
    }

    const hasId = q.id !== undefined && q.id !== null && q.id !== '';
    const refId = hasId ? generateReferenceId(examId, q.id) : '—';

    const questionText = q.question ? String(q.question) : '';
    const snippet =
      questionText.length > 60
        ? questionText.slice(0, 60) + '…'
        : questionText || '(no question text)';

    // Resolved lifecycle values (what will actually be imported)
    const importStatus  = VALID_STATUSES.includes(q.status) ? q.status : 'new';
    const importReviewStatus = VALID_REVIEW_STATUSES.includes(q.reviewStatus) ? q.reviewStatus : 'unreviewed';

    return {
      raw: q,
      referenceId: refId,
      snippet,
      status: validation.status,
      errors: validation.errors,
      importStatus,
      importReviewStatus,
    };
  });
}

// ── normalizeImportRow ────────────────────────────────────────────────────────

/**
 * Maps a valid raw import row to the app's normalized question shape.
 * Mirrors normalizeQuestion() in questionService.js.
 * Should only be called on rows with status === 'valid'.
 *
 * Lifecycle defaults:
 *   status       → raw.status       || 'new'
 *   reviewStatus → raw.reviewStatus || 'unreviewed'
 *   sourceBatchId    → raw.sourceBatchId    || null
 *   sourceGenerator  → raw.sourceGenerator  || null
 *
 * @param {object} raw    - a valid raw row
 * @param {string} examId - 'pmp' | 'cpmai'
 * @returns {object} normalized question
 */
export function normalizeImportRow(raw, examId) {
  const importStatus = VALID_STATUSES.includes(raw.status) ? raw.status : 'new';
  const importReviewStatus = VALID_REVIEW_STATUSES.includes(raw.reviewStatus) ? raw.reviewStatus : 'unreviewed';

  // Canonical nested-metadata read. Reads from raw.metadata.<key> first,
  // falls back to raw.<key> at root. Shared fields apply to every cert;
  // cert-specific keys are picked up further below.
  const m = (raw.metadata && typeof raw.metadata === 'object') ? raw.metadata : {};
  const rawEco          = m.ecoDomain      ?? raw.ecoDomain;
  const rawPracticeArea = m.practiceArea   ?? raw.practiceArea;
  const rawCognitive    = m.cognitiveLevel ?? raw.cognitiveLevel;
  const rawDifficulty   = m.difficulty     ?? raw.difficulty;
  const rawExamWeight   = m.examWeight     ?? raw.examWeight;
  const rawScenario     = m.scenarioBased  ?? raw.scenarioBased;
  const rawEcoTask      = m.ecoTask        ?? raw.ecoTask;
  const rawKnowledge    = m.knowledgeArea  ?? raw.knowledgeArea;
  const rawArtifact     = m.artifact       ?? raw.artifact;
  // PMP canonical metadata (migration 034). Same nested-first lookup pattern.
  const rawPerformance  = m.performanceDomain ?? raw.performanceDomain;
  const rawDelivery     = m.deliveryContext   ?? raw.deliveryContext;
  const rawProcessGroup = m.processGroup      ?? raw.processGroup;
  const rawPmpTask      = m.task              ?? raw.task;
  // CPMAI canonical metadata (migration 034). All fields free-form today.
  const rawModule       = m.module          ?? raw.module;
  const rawCapability   = m.capability      ?? raw.capability;
  const rawLifecycle    = m.lifecycleStage  ?? raw.lifecycleStage;
  const rawAiConcept    = m.aiConcept       ?? raw.aiConcept;

  // ── PMP-only taxonomy pass-through ────────────────────────────────────
  // For exam='pmp': keep the value only if it's a known enum, otherwise drop
  // it to null (buildPreview has already flagged invalid values as errors;
  // this is the belt-and-braces guard for any caller that bypasses preview).
  // For any other exam: always null — these fields are PMP-only today.
  const performanceDomain = examId === 'pmp' && VALID_PERFORMANCE_DOMAINS.includes(rawPerformance)
    ? rawPerformance : null;
  const deliveryContext   = examId === 'pmp' && VALID_DELIVERY_CONTEXTS.includes(rawDelivery)
    ? rawDelivery   : null;
  const processGroup      = examId === 'pmp' && VALID_PROCESS_GROUPS.includes(rawProcessGroup)
    ? rawProcessGroup : null;
  const pmpTask           = examId === 'pmp' && typeof rawPmpTask === 'string' && rawPmpTask.trim()
    ? rawPmpTask.trim() : null;
  const cpmaiModule       = examId === 'cpmai' && typeof rawModule     === 'string' && rawModule.trim()
    ? rawModule.trim() : null;
  const cpmaiCapability   = examId === 'cpmai' && typeof rawCapability === 'string' && rawCapability.trim()
    ? rawCapability.trim() : null;
  const cpmaiLifecycle    = examId === 'cpmai' && typeof rawLifecycle  === 'string' && rawLifecycle.trim()
    ? rawLifecycle.trim() : null;
  const cpmaiAiConcept    = examId === 'cpmai' && typeof rawAiConcept  === 'string' && rawAiConcept.trim()
    ? rawAiConcept.trim() : null;

  // For PMI-ACP, category and ecoDomain are kept in sync via the 4-domain
  // ECO vocabulary. Legacy 7-domain `category` values are forward-mapped.
  // If only one of (category, ecoDomain) is set, the other is filled to
  // match. If both are set and disagree, ecoDomain wins.
  let normalizedCategory = raw.category || raw.source || '';
  let ecoDomain = null;
  if (examId === 'pmi-acp') {
    const catCanon = forwardMapPmiAcpCategory(normalizedCategory);
    const ecoCanon = VALID_ECO_DOMAINS.includes(rawEco) ? rawEco : null;
    if (ecoCanon) {
      ecoDomain = ecoCanon;
      normalizedCategory = ecoCanon;
    } else if (catCanon) {
      ecoDomain = catCanon;
      normalizedCategory = catCanon;
    } else {
      normalizedCategory = '';
    }
  } else if (VALID_ECO_DOMAINS.includes(rawEco)) {
    // Non-PMI-ACP rows shouldn't have ecoDomain at all, but tolerate it.
    ecoDomain = rawEco;
  }

  const practiceArea = (examId === 'pmi-acp' && VALID_PRACTICE_AREAS.includes(rawPracticeArea))
    ? rawPracticeArea : null;
  const cognitiveLevel = VALID_COGNITIVE_LEVELS.includes(rawCognitive)
    ? rawCognitive : null;
  const difficulty = VALID_DIFFICULTIES.includes(rawDifficulty)
    ? rawDifficulty : null;
  // examWeight is a shared canonical field across all certs (migration 034 update).
  const examWeight = VALID_EXAM_WEIGHTS.includes(rawExamWeight)
    ? rawExamWeight : null;
  const scenarioBased = rawScenario === true || rawScenario === 'true';
  const ecoTask       = typeof rawEcoTask    === 'string' && rawEcoTask.trim()    ? rawEcoTask.trim()    : null;
  const knowledgeArea = typeof rawKnowledge  === 'string' && rawKnowledge.trim()  ? rawKnowledge.trim()  : null;
  const artifact      = typeof rawArtifact   === 'string' && rawArtifact.trim()   ? rawArtifact.trim()   : null;

  const rawFocus = metaField(raw, 'focusArea') ?? raw.focusArea;
  const focusArea = typeof rawFocus === 'string' && rawFocus.trim() ? rawFocus.trim() : null;

  return {
    id:              raw.id,
    referenceId:     generateReferenceId(examId, raw.id),
    type:            raw.type || 'multiple_choice',
    question:        raw.question,
    choices:         raw.choices,
    correctAnswers:  raw.correctAnswers,
    explanation:     raw.explanation || '',
    confidence:      raw.confidence || 'medium',
    category:        normalizedCategory,
    // Source / Reference (migration 040). Canonical nested-metadata first,
    // then root-level fallback. null when absent.
    source:          metaField(raw, 'source') ?? raw.source ?? null,
    // Explicit flag wins; otherwise derive from the declared type so JSON
    // that only sets type: 'multiple_select' renders checkboxes correctly.
    multiSelect:     raw.ui?.multiSelect ?? raw.multiSelect ?? (raw.type === 'multiple_select'),
    status:          importStatus,
    reviewStatus:    importReviewStatus,
    sourceBatchId:   raw.sourceBatchId || null,
    sourceGenerator: raw.sourceGenerator || null,
    performanceDomain,
    deliveryContext,
    processGroup,
    task: pmpTask,
    // CPMAI canonical metadata (migration 034). Top-level for legacy callers.
    module:          cpmaiModule,
    capability:      cpmaiCapability,
    lifecycleStage:  cpmaiLifecycle,
    aiConcept:       cpmaiAiConcept,
    // PMI-ACP canonical metadata (migrations 032 + 033). Kept at the top
    // level so existing call sites (App.js handleImport, etc.) continue to
    // work; the nested `metadata` object on the right is also published for
    // downstream code that prefers the canonical shape.
    ecoDomain,
    ecoTask,
    practiceArea,
    knowledgeArea,
    artifact,
    cognitiveLevel,
    difficulty,
    scenarioBased,
    examWeight,
    metadata: {
      // Shared
      cognitiveLevel,
      difficulty,
      scenarioBased,
      examWeight,
      knowledgeArea,
      // PMP
      performanceDomain,
      deliveryContext,
      processGroup,
      task: pmpTask,
      // PMI-ACP
      ecoDomain,
      ecoTask,
      practiceArea,
      artifact,
      // CPMAI
      module:         cpmaiModule,
      capability:     cpmaiCapability,
      lifecycleStage: cpmaiLifecycle,
      aiConcept:      cpmaiAiConcept,
      // Secondary dimension (PMOCP)
      focusArea,
    },
    // Phase 2 (migration 012).  Strict boolean — anything other than `true`
    // becomes false so the column never gets a truthy non-boolean.
    // Secondary dimension (migration 042/073) — e.g. PMOCP focus_area.
    focusArea,
    isExperimental:  raw.isExperimental === true,
    // Phase 3 (migration 013) — visual exhibit object.  Sanitize: keep src/alt
    // strings only, drop everything else.  Returns null when image is absent
    // or unusable, so non-visual questions stay untouched.
    image: (raw.image && typeof raw.image === 'object' && raw.image.src)
      ? { src: String(raw.image.src), alt: raw.image.alt ? String(raw.image.alt) : '' }
      : null,
  };
}
